import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Megaphone,
  AlertTriangle,
  Tag,
  Calendar,
  User,
} from "lucide-react";
import { useAnnouncements } from "../../hooks";

const CATEGORY_LABELS = {
  general: "General",
  policy: "Policy Update",
  event: "Event",
  hr_update: "HR Update",
  holiday: "Holiday",
  achievement: "Achievement",
};

const priorityStyles = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  important: "bg-amber-100 text-amber-700 border-amber-200",
  normal: "bg-slate-100 text-slate-600 border-slate-200",
};

const categoryStyles = {
  general: "bg-blue-50 text-blue-600",
  policy: "bg-violet-50 text-violet-600",
  event: "bg-teal-50 text-teal-600",
  hr_update: "bg-rose-50 text-rose-600",
  holiday: "bg-green-50 text-green-600",
  achievement: "bg-amber-50 text-amber-600",
};

const MyAnnouncements = () => {
  const { currentBusiness } = useAuth();
  
  // Use the custom hook for data fetching
  const { announcements, loading } = useAnnouncements(currentBusiness?.id);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-announcements-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">Announcements</h1>
        <p className="text-slate-500">Company updates and newsletters</p>
      </div>

      {announcements.length === 0 ? (
        <Card className="card-soft">
          <CardContent className="py-16 text-center">
            <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No announcements yet</p>
            <p className="text-sm text-slate-400 mt-1">Check back later for company updates</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((a) => (
            <Card key={a.id} className="card-soft hover:shadow-md transition-shadow" data-testid={`my-announcement-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge variant="outline" className={priorityStyles[a.priority] || priorityStyles.normal}>
                    {a.priority === "urgent" && <AlertTriangle className="h-3 w-3 mr-1" />}
                    {a.priority?.charAt(0).toUpperCase() + a.priority?.slice(1)}
                  </Badge>
                  <Badge variant="outline" className={categoryStyles[a.category] || categoryStyles.general}>
                    <Tag className="h-3 w-3 mr-1" />
                    {CATEGORY_LABELS[a.category] || a.category}
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-1">{a.title}</h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{a.content}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {a.author_name || "Admin"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(a.created_at)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAnnouncements;
